import { useEffect, useMemo, useState } from "react";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarBlank, DownloadSimple, FileText, Receipt, Scales, Coins, Tag, Package, SignOut, Percent } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function firstOfMonth(d = new Date()) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; }
function lastOfMonth(d = new Date()) {
  const end = new Date(d.getFullYear(), d.getMonth()+1, 0);
  return `${end.getFullYear()}-${String(end.getMonth()+1).padStart(2,"0")}-${String(end.getDate()).padStart(2,"0")}`;
}
function firstOfYear(y) { return `${y}-01-01`; }
function lastOfYear(y) { return `${y}-12-31`; }
function ymd(iso) { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } }

export default function SteuerberaterPortal() {
  const { user, logout } = useAuth();
  const nav = (to) => window.location.assign(to);
  const now = new Date();
  const [dfrom, setDfrom] = useState(firstOfYear(now.getFullYear()));
  const [dto,   setDto]   = useState(lastOfYear(now.getFullYear()));
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [vatReport, setVatReport] = useState(null);
  const [vatYear, setVatYear] = useState(now.getFullYear());
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    Promise.all([
      api.get("/steuerberater/summary", { params: { dfrom, dto } }).then(r => setSummary(r.data)),
      api.get("/steuerberater/revenue",  { params: { dfrom, dto } }).then(r => setRevenue(r.data)),
      api.get("/steuerberater/expenses", { params: { dfrom, dto } }).then(r => setExpenses(r.data)),
    ]).finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dfrom, dto]);

  useEffect(() => {
    api.get("/steuerberater/vat-report", { params: { year: vatYear } }).then(r => setVatReport(r.data)).catch(()=>setVatReport(null));
  }, [vatYear]);

  const setPreset = (type) => {
    const y = now.getFullYear();
    if (type === "month") { setDfrom(firstOfMonth(now)); setDto(lastOfMonth(now)); }
    else if (type === "lastmonth") {
      const d = new Date(y, now.getMonth()-1, 1);
      setDfrom(firstOfMonth(d)); setDto(lastOfMonth(d));
    }
    else if (type === "q1") { setDfrom(`${y}-01-01`); setDto(`${y}-03-31`); }
    else if (type === "q2") { setDfrom(`${y}-04-01`); setDto(`${y}-06-30`); }
    else if (type === "q3") { setDfrom(`${y}-07-01`); setDto(`${y}-09-30`); }
    else if (type === "q4") { setDfrom(`${y}-10-01`); setDto(`${y}-12-31`); }
    else if (type === "year") { setDfrom(firstOfYear(y)); setDto(lastOfYear(y)); }
    else if (type === "lastyear") { setDfrom(firstOfYear(y-1)); setDto(lastOfYear(y-1)); }
  };

  const dl = (path) => window.open(`${API_BASE}/api${path}${path.includes("?") ? "&" : "?"}dfrom=${dfrom}&dto=${dto}`, "_blank");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="stb-portal">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center">
              <Scales size={22} weight="fill" color="#fff"/>
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Steuerberater-Portal</h1>
              <p className="text-xs text-muted-foreground">MainSpessartBlick · {user?.name} · Read-Only</p>
            </div>
          </div>
          <Button onClick={async ()=>{ await logout(); nav("/login"); }} variant="outline" className="rounded-lg" data-testid="stb-logout">
            <SignOut size={16} weight="bold"/><span className="ml-2">Abmelden</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Zeitraum */}
        <section className="msb-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarBlank size={18} weight="fill" className="text-primary"/>
            <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Zeitraum</h2>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              ["month","Aktueller Monat"],["lastmonth","Letzter Monat"],
              ["q1","Q1"],["q2","Q2"],["q3","Q3"],["q4","Q4"],
              ["year",`Jahr ${now.getFullYear()}`],["lastyear",`Jahr ${now.getFullYear()-1}`],
            ].map(([k,l]) => (
              <button key={k} onClick={()=>setPreset(k)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition font-medium" data-testid={`stb-preset-${k}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Von</Label>
              <Input type="date" value={dfrom} onChange={(e)=>setDfrom(e.target.value)} className="h-10 rounded-lg mt-1 w-44" data-testid="stb-from"/>
            </div>
            <div>
              <Label className="text-xs">Bis</Label>
              <Input type="date" value={dto} onChange={(e)=>setDto(e.target.value)} className="h-10 rounded-lg mt-1 w-44" data-testid="stb-to"/>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button onClick={()=>dl("/steuerberater/export/revenue.csv")} variant="outline" className="rounded-lg" data-testid="stb-dl-revenue">
                <DownloadSimple size={16} weight="bold"/><span className="ml-2">CSV Einnahmen</span>
              </Button>
              <Button onClick={()=>dl("/steuerberater/export/expenses.csv")} variant="outline" className="rounded-lg" data-testid="stb-dl-expenses">
                <DownloadSimple size={16} weight="bold"/><span className="ml-2">CSV Ausgaben</span>
              </Button>
              <Button onClick={()=>dl("/steuerberater/export/zip")} className="rounded-lg bg-primary text-white" data-testid="stb-dl-zip">
                <Package size={16} weight="bold"/><span className="ml-2">Alles als ZIP</span>
              </Button>
            </div>
          </div>
        </section>

        {/* KPIs */}
        {summary && (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stb-kpis">
            <Kpi icon={<Coins size={22} weight="fill"/>} label="Einnahmen" value={euro(summary.revenue_total)} hint={`${summary.revenue_count} Rechnungen`} color="#059669"/>
            <Kpi icon={<Receipt size={22} weight="fill"/>} label="Ausgaben"  value={euro(summary.expenses_total)} hint={`${summary.expenses_count} Positionen`} color="#dc2626"/>
            <Kpi icon={<Scales size={22} weight="fill"/>} label="Gewinn"    value={euro(summary.profit)} hint={summary.profit >= 0 ? "Einnahmen ≥ Ausgaben" : "Einnahmen < Ausgaben"} color={summary.profit >= 0 ? "#059669" : "#dc2626"}/>
          </section>
        )}

        {/* Ausgaben nach Kategorie */}
        {summary?.expenses_by_category && Object.keys(summary.expenses_by_category).length > 0 && (
          <section className="msb-card" data-testid="stb-cats">
            <div className="flex items-center gap-2 mb-3">
              <Tag size={16} weight="bold" className="text-primary"/>
              <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Ausgaben nach Kategorie</h2>
            </div>
            <div className="space-y-2">
              {Object.entries(summary.expenses_by_category).sort(([,a],[,b])=>b-a).map(([cat, sum]) => {
                const pct = summary.expenses_total ? Math.round(sum/summary.expenses_total*100) : 0;
                return (
                  <div key={cat} className="flex items-center gap-3 text-sm">
                    <div className="min-w-[160px] font-semibold">{cat}</div>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{width: `${pct}%`}}/>
                    </div>
                    <div className="min-w-[100px] text-right font-mono font-bold">{euro(sum)}</div>
                    <div className="min-w-[45px] text-right text-xs text-muted-foreground">{pct}%</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* MwSt-Voranmeldung */}
        {vatReport && (
          <section className="msb-card p-0 overflow-hidden" data-testid="stb-vat-report">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Percent size={18} weight="duotone" className="text-indigo-700"/>
                </div>
                <div>
                  <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>MwSt-Übersicht · für Umsatzsteuer-Voranmeldung</h2>
                  <p className="text-xs text-muted-foreground">Vorsteuer aus Ausgaben, monatlich aufgeschlüsselt · Basis für Ihre UStVA</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select value={vatYear} onChange={(e)=>setVatYear(parseInt(e.target.value,10))} className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="vat-year">
                  {[now.getFullYear(), now.getFullYear()-1, now.getFullYear()-2].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button onClick={()=>window.open(`${API_BASE}/api/steuerberater/export/vat-report.csv?year=${vatYear}`, "_blank")} variant="outline" className="rounded-lg h-9" data-testid="vat-dl-csv">
                  <DownloadSimple size={14} weight="bold"/><span className="ml-1.5">CSV</span>
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="text-left p-3">Monat</th>
                    <th className="text-right p-3 border-l-4 border-emerald-200 bg-emerald-50/50">Netto 7 %</th>
                    <th className="text-right p-3 bg-emerald-50/50">MwSt 7 %</th>
                    <th className="text-right p-3 border-l-4 border-sky-200 bg-sky-50/50">Netto 19 %</th>
                    <th className="text-right p-3 bg-sky-50/50">MwSt 19 %</th>
                    <th className="text-right p-3 border-l-4 border-indigo-200 bg-indigo-50/50 font-bold">Vorsteuer</th>
                    <th className="text-right p-3">Brutto ges.</th>
                    <th className="text-center p-3">Belege</th>
                  </tr>
                </thead>
                <tbody>
                  {vatReport.months.map(m => {
                    const hasData = m.total > 0;
                    return (
                      <tr key={m.month} className={`border-t border-border ${!hasData ? "text-slate-400" : ""}`}>
                        <td className="p-3 font-semibold">{m.label} {vatReport.year}</td>
                        <td className="p-3 text-right font-mono">{euro(m.mwst_7_net)}</td>
                        <td className="p-3 text-right font-mono text-emerald-700">{euro(m.mwst_7_amount)}</td>
                        <td className="p-3 text-right font-mono">{euro(m.mwst_19_net)}</td>
                        <td className="p-3 text-right font-mono text-sky-700">{euro(m.mwst_19_amount)}</td>
                        <td className="p-3 text-right font-mono font-bold text-indigo-700 bg-indigo-50/30">{euro(m.vorsteuer_total)}</td>
                        <td className="p-3 text-right font-mono">{euro(m.total)}</td>
                        <td className="p-3 text-center text-xs">{m.count}</td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                    <td className="p-3">GESAMT {vatReport.year}</td>
                    <td className="p-3 text-right font-mono">{euro(vatReport.ytd.mwst_7_net)}</td>
                    <td className="p-3 text-right font-mono text-emerald-700">{euro(vatReport.ytd.mwst_7_amount)}</td>
                    <td className="p-3 text-right font-mono">{euro(vatReport.ytd.mwst_19_net)}</td>
                    <td className="p-3 text-right font-mono text-sky-700">{euro(vatReport.ytd.mwst_19_amount)}</td>
                    <td className="p-3 text-right font-mono text-indigo-700 bg-indigo-100">{euro(vatReport.ytd.vorsteuer_total)}</td>
                    <td className="p-3 text-right font-mono">{euro(vatReport.ytd.total)}</td>
                    <td className="p-3 text-center text-xs">{vatReport.ytd.count}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="p-3 text-[11px] text-muted-foreground border-t border-slate-100 bg-slate-50/40">
              <strong>Hinweis für die UStVA:</strong> Die Spalte „Vorsteuer" zeigt die abziehbaren Vorsteuer-Beträge (§ 15 UStG). Netto- und Steuer-Werte wurden per KI-OCR aus den Belegen extrahiert und pro Beleg geprüft/freigegeben. Umsatzsteuer aus Ausgangs-Rechnungen wird separat in „Einnahmen · Rechnungen" ausgewiesen.
            </div>
          </section>
        )}

        {/* Einnahmen */}
        <section className="msb-card p-0 overflow-hidden" data-testid="stb-revenue">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Einnahmen · Rechnungen</h2>
              <p className="text-xs text-muted-foreground">Alle Rechnungen im gewählten Zeitraum</p>
            </div>
            {revenue && <div className="text-sm font-bold text-emerald-700">{euro(revenue.total)}</div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="text-left p-3">Datum</th>
                  <th className="text-left p-3">Rechnung</th>
                  <th className="text-left p-3">Gast</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Betrag</th>
                  <th className="text-right p-3">PDF</th>
                </tr>
              </thead>
              <tbody>
                {(!revenue?.invoices?.length && !busy) && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Keine Rechnungen im gewählten Zeitraum.</td></tr>}
                {(revenue?.invoices || []).map(i => (
                  <tr key={i.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3 whitespace-nowrap">{ymd(i.invoice_date)}</td>
                    <td className="p-3 font-mono font-semibold">{i.number}</td>
                    <td className="p-3">{i.guest_name}</td>
                    <td className="p-3 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${i.status === "bezahlt" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{i.status || "erstellt"}</span>
                    </td>
                    <td className="p-3 text-right font-mono font-bold whitespace-nowrap">{euro(i.total_amount || i.amount)}</td>
                    <td className="p-3 text-right">
                      <a href={`${API_BASE}/api/invoices/${i.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs" data-testid={`stb-inv-pdf-${i.id}`}>
                        <FileText size={13} weight="bold"/> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Ausgaben */}
        <section className="msb-card p-0 overflow-hidden" data-testid="stb-expenses">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Ausgaben · Belege</h2>
              <p className="text-xs text-muted-foreground">Alle Ausgabenpositionen im gewählten Zeitraum · Belege einzeln abrufbar</p>
            </div>
            {expenses && <div className="text-sm font-bold text-red-700">{euro(expenses.total)}</div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="text-left p-3">Datum</th>
                  <th className="text-left p-3">Zweck</th>
                  <th className="text-left p-3">Kategorie</th>
                  <th className="text-right p-3">Betrag</th>
                  <th className="text-right p-3">Beleg</th>
                </tr>
              </thead>
              <tbody>
                {(!expenses?.expenses?.length && !busy) && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Keine Ausgaben im gewählten Zeitraum.</td></tr>}
                {(expenses?.expenses || []).map(e => (
                  <tr key={e.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3 whitespace-nowrap">{ymd(e.date)}</td>
                    <td className="p-3">
                      <div className="font-semibold">{e.purpose}</div>
                      {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                    </td>
                    <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded-full bg-slate-100">{e.category}</span></td>
                    <td className="p-3 text-right font-mono font-bold text-red-700 whitespace-nowrap">{euro(e.amount)}</td>
                    <td className="p-3 text-right">
                      {e.receipt_file ? (
                        <a href={`${API_BASE}/api/expenses/${e.id}/receipt`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-xs" data-testid={`stb-receipt-${e.id}`}>
                          <FileText size={13} weight="bold"/> ansehen
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="text-center text-xs text-muted-foreground pt-4 pb-6">
          MainSpessartBlick Cockpit · Alle Zahlen aus dem produktiven System · Nur Lesezugriff
        </div>
      </main>
    </div>
  );
}

function Kpi({ icon, label, value, hint, color }) {
  return (
    <div className="msb-card relative overflow-hidden" data-testid={`stb-kpi-${label.toLowerCase()}`}>
      <div className="absolute left-0 top-0 h-full w-1" style={{background: color}}/>
      <div className="flex items-center gap-2 text-muted-foreground">
        <span style={{color}}>{icon}</span>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-bold mt-2" style={{fontFamily:"'Work Sans',sans-serif"}}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
